import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { CANNONS, PROJECTILES } from '../../src/game/data.js';
import {
  createDefaultCalibrationWheel,
} from '../../src/tools/vehicleCalibrator.js';
import {
  DEFAULT_IMPORTED_VEHICLE_VISUAL_SCALE,
  VehicleAssetRig,
  applyVehicleBodyFinish,
  prepareVehicleFinishMaterials,
} from '../../src/tools/vehicleProfile.js';
import {
  inspectOneShotSample,
  previewOneShot,
  previewOneShotSample,
} from './proceduralSound.js';
import {
  cloneGlbPreview,
  formatGlbTriangles,
  loadCatalogGlb,
} from './glbImportUtils.js';

const $=(root,selector)=>root.querySelector(selector);
const $$=(root,selector)=>[...root.querySelectorAll(selector)];
const AUTO_ROTATE_STORAGE_KEY='toybox-catalog-studio:cannon-auto-rotate';
const PREVIEW_VEHICLE_STORAGE_KEY='toybox-catalog-studio:cannon-preview-vehicle';
const CANNON_STYLES=new Set(['single','twin','bubble','rail']);

function cleanId(value){
  return String(value||'canon-personalizado')
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-zA-Z0-9_-]+/g,'-')
    .replace(/^-+|-+$/g,'')
    .toLowerCase()||'canon-personalizado';
}

function readAutoRotatePreference(){
  try{
    const stored=window.localStorage.getItem(AUTO_ROTATE_STORAGE_KEY);
    return stored===null?true:stored==='true';
  }catch{
    return true;
  }
}

function saveAutoRotatePreference(enabled){
  try{
    window.localStorage.setItem(AUTO_ROTATE_STORAGE_KEY,String(enabled));
  }catch{
    // La vista sigue funcionando aunque el navegador bloquee localStorage.
  }
}

function readPreviewVehiclePreference(){
  try{
    return window.localStorage.getItem(PREVIEW_VEHICLE_STORAGE_KEY)||'';
  }catch{
    return '';
  }
}

function savePreviewVehiclePreference(id){
  try{
    if(id)window.localStorage.setItem(PREVIEW_VEHICLE_STORAGE_KEY,id);
    else window.localStorage.removeItem(PREVIEW_VEHICLE_STORAGE_KEY);
  }catch{
    // El selector sigue funcionando aunque el navegador bloquee localStorage.
  }
}

function cloneDesign(source={}){
  const style=CANNON_STYLES.has(source.style)?source.style:'single';
  const styleDefaults={
    single:{barrelLength:.72,barrelRadius:.13},
    twin:{barrelLength:.66,barrelRadius:.085},
    bubble:{barrelLength:.57,barrelRadius:.13},
    rail:{barrelLength:.82,barrelRadius:.075},
  }[style];
  return {
    style,
    color:source.color||'#d9dde2',
    accent:source.accent||'#ffcf43',
    baseColor:source.baseColor||'#30303b',
    metalness:Number(source.metalness??.55),
    roughness:Number(source.roughness??.32),
    baseRadius:Number(source.baseRadius??.26),
    baseTopRadius:Number(source.baseTopRadius??.20),
    baseHeight:Number(source.baseHeight??.14),
    barrelLength:Number(source.barrelLength??styleDefaults.barrelLength),
    barrelRadius:Number(source.barrelRadius??styleDefaults.barrelRadius),
    barrelTaper:Number(source.barrelTaper??.82),
    muzzleScale:Number(source.muzzleScale??1.28),
    muzzleLength:Number(source.muzzleLength??.09),
    twinSeparation:Number(source.twinSeparation??.23),
    bridgeHeight:Number(source.bridgeHeight??.10),
    tankRadius:Number(source.tankRadius??.23),
    tankStretch:Number(source.tankStretch??1.12),
    railSeparation:Number(source.railSeparation??.18),
    railWidth:Number(source.railWidth??.075),
    tipWidth:Number(source.tipWidth??.30),
    tipHeight:Number(source.tipHeight??.12),
    tipLength:Number(source.tipLength??.13),
    modelScale:Number(source.modelScale??1),
    modelRotationX:Number(source.modelRotationX??0),
    modelRotationY:Number(source.modelRotationY??0),
    modelRotationZ:Number(source.modelRotationZ??0),
    defaultProjectileId:source.defaultProjectileId||PROJECTILES[0].id,
    soundWave:source.soundWave||'square',
    soundTone:Number(source.soundTone??520),
    soundDuration:Number(source.soundDuration??.075),
    soundFilter:Number(source.soundFilter??2200),
    soundNoiseGain:Number(source.soundNoiseGain??.025),
    soundToneGain:Number(source.soundToneGain??.075),
    soundSampleVolume:Number(source.soundSampleVolume??.5),
    soundSampleRate:Number(source.soundSampleRate??1),
  };
}

function disposeObject(object){
  object?.traverse((child)=>{
    if(!child.isMesh)return;
    child.geometry?.dispose();
    const materials=Array.isArray(child.material)?child.material:[child.material];
    materials.filter(Boolean).forEach((material)=>material.dispose?.());
  });
}

function roundedBox(width,height,depth,material,radius=.03){
  return new THREE.Mesh(
    new RoundedBoxGeometry(width,height,depth,3,Math.min(radius,width*.25,height*.25,depth*.25)),
    material,
  );
}

function createCannon(design){
  const root=new THREE.Group();
  root.name='procedural_cannon_preview';
  const bodyMaterial=new THREE.MeshPhysicalMaterial({
    color:design.color,
    metalness:design.metalness,
    roughness:design.roughness,
    clearcoat:.4,
  });
  const accentMaterial=new THREE.MeshPhysicalMaterial({
    color:design.accent,
    metalness:Math.max(.2,design.metalness-.08),
    roughness:Math.min(.85,design.roughness+.02),
    clearcoat:.55,
  });
  const baseMaterial=new THREE.MeshStandardMaterial({
    color:design.baseColor,
    metalness:.42,
    roughness:.46,
  });
  const base=new THREE.Mesh(
    new THREE.CylinderGeometry(
      design.baseTopRadius,
      design.baseRadius,
      design.baseHeight,
      24,
    ),
    baseMaterial,
  );
  base.position.y=design.baseHeight*.5;
  base.castShadow=true;
  root.add(base);
  const barrelY=design.baseHeight+.08;

  const addBarrel=(x)=>{
    const barrel=new THREE.Mesh(
      new THREE.CylinderGeometry(
        design.barrelRadius*design.barrelTaper,
        design.barrelRadius,
        design.barrelLength,
        20,
      ),
      bodyMaterial,
    );
    barrel.rotation.x=Math.PI/2;
    barrel.position.set(x,barrelY,-design.barrelLength*.44);
    barrel.castShadow=true;
    const muzzle=new THREE.Mesh(
      new THREE.CylinderGeometry(
        design.barrelRadius*design.muzzleScale,
        design.barrelRadius*design.muzzleScale,
        design.muzzleLength,
        20,
      ),
      accentMaterial,
    );
    muzzle.rotation.x=Math.PI/2;
    muzzle.position.set(x,barrelY,-design.barrelLength*.91);
    muzzle.castShadow=true;
    root.add(barrel,muzzle);
  };

  if(design.style==='twin'){
    const halfSeparation=design.twinSeparation*.5;
    addBarrel(-halfSeparation);
    addBarrel(halfSeparation);
    const bridge=roundedBox(
      design.twinSeparation+design.barrelRadius*2.35,
      design.bridgeHeight,
      .18,
      accentMaterial,
      .035,
    );
    bridge.position.set(0,barrelY,-.12);
    root.add(bridge);
  }else if(design.style==='bubble'){
    const tank=new THREE.Mesh(
      new THREE.SphereGeometry(design.tankRadius,24,16),
      accentMaterial,
    );
    tank.scale.z=design.tankStretch;
    tank.position.set(0,design.baseHeight+design.tankRadius*.72,-.06);
    tank.castShadow=true;
    root.add(tank);
    addBarrel(0);
  }else if(design.style==='rail'){
    const halfSeparation=design.railSeparation*.5;
    [-halfSeparation,halfSeparation].forEach((x)=>{
      const rail=roundedBox(
        design.railWidth,
        design.railWidth,
        design.barrelLength,
        bodyMaterial,
        .025,
      );
      rail.position.set(x,barrelY,-design.barrelLength*.43);
      rail.castShadow=true;
      root.add(rail);
    });
    const tip=roundedBox(
      design.tipWidth,
      design.tipHeight,
      design.tipLength,
      accentMaterial,
      .04,
    );
    tip.position.set(0,barrelY,-design.barrelLength*.9);
    tip.castShadow=true;
    root.add(tip);
  }else{
    addBarrel(0);
  }
  return root;
}

export class CannonEditor{
  constructor(screen,callbacks={}){
    this.screen=screen;
    this.callbacks=callbacks;
    this.canvas=$(screen,'#cannon-editor-canvas');
    this.status=$(screen,'#cannon-editor-message');
    this.saveButton=$(screen,'#cannon-editor-save');
    this.fileInput=$(screen,'#cannon-editor-file');
    this.fileName=$(screen,'#cannon-editor-file-name');
    this.fileInfo=$(screen,'#cannon-editor-file-info');
    this.soundFileInput=$(screen,'#cannon-editor-sound-file');
    this.soundFileName=$(screen,'#cannon-editor-sound-file-name');
    this.soundFileInfo=$(screen,'#cannon-editor-sound-file-info');
    this.testSoundButton=$(screen,'#cannon-editor-test-sound');
    this.templateSelect=$(screen,'#cannon-editor-template');
    this.styleSelect=$(screen,'#cannon-editor-style');
    this.defaultProjectileSelect=$(screen,'#cannon-editor-default-projectile');
    this.vehicleSelect=$(screen,'#cannon-editor-vehicle');
    this.vehicleControls=$(screen,'[data-cannon-vehicle-controls]');
    this.vehicleNote=$(screen,'#cannon-editor-vehicle-note');
    this.autoRotateInput=$(screen,'#cannon-editor-auto-rotate');
    this.autoRotateInput.checked=readAutoRotatePreference();
    this.active=false;
    this.mode='procedural';
    this.previewMode='cannon';
    this.vehicleOptionsLoaded=false;
    this.vehicleLoadToken=0;
    this.mountedVehicle=null;
    this.mountedRig=null;
    this.mountedCannonMount=null;
    this.mountedCannonObject=null;
    this.assetFile=null;
    this.assetStats=null;
    this.soundMode='procedural';
    this.soundFile=null;
    this.soundStats=null;
    this.design=cloneDesign(CANNONS[0]);
    this.initScene();
    this.setProjectileOptions(PROJECTILES);
    this.bindInterface();
    this.syncControls();
    this.rebuild();
    this.animate();
  }

  initScene(){
    this.renderer=new THREE.WebGLRenderer({
      canvas:this.canvas,
      antialias:true,
      powerPreference:'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio,1.75));
    this.renderer.outputColorSpace=THREE.SRGBColorSpace;
    this.renderer.toneMapping=THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure=1.08;
    this.renderer.shadowMap.enabled=true;

    this.scene=new THREE.Scene();
    this.scene.background=new THREE.Color('#17172a');
    this.scene.fog=new THREE.Fog('#17172a',8,18);
    this.camera=new THREE.PerspectiveCamera(38,1,.03,40);
    this.camera.position.set(3.2,2.25,4.2);
    this.controls=new OrbitControls(this.camera,this.canvas);
    this.controls.enableDamping=true;
    this.controls.dampingFactor=.075;
    this.controls.target.set(0,.28,-.2);
    this.controls.minDistance=1.8;
    this.controls.maxDistance=8;

    this.scene.add(new THREE.HemisphereLight(0xe8f3ff,0x25213a,2.2));
    const key=new THREE.DirectionalLight(0xffedd3,4.5);
    key.position.set(-4,7,-5);
    key.castShadow=true;
    key.shadow.mapSize.set(1024,1024);
    this.scene.add(key);
    const fill=new THREE.DirectionalLight(0x63cfff,2.2);
    fill.position.set(5,3,4);
    this.scene.add(fill);

    this.floor=new THREE.Mesh(
      new THREE.CircleGeometry(2.25,64),
      new THREE.MeshStandardMaterial({color:'#201c34',roughness:.9,metalness:.05}),
    );
    this.floor.rotation.x=-Math.PI/2;
    this.floor.receiveShadow=true;
    this.scene.add(this.floor);
    this.grid=new THREE.GridHelper(6,18,0x4dddec,0x3b3551);
    this.grid.position.y=.005;
    this.grid.material.transparent=true;
    this.grid.material.opacity=.28;
    this.scene.add(this.grid);
    this.previewMount=new THREE.Group();
    this.scene.add(this.previewMount);

    this.loader=new GLTFLoader();
    this.loader.setMeshoptDecoder(MeshoptDecoder);
    this.dracoLoader=new DRACOLoader();
    this.dracoLoader.setDecoderPath(`${import.meta.env.BASE_URL}draco/`);
    this.loader.setDRACOLoader(this.dracoLoader);
  }

  bindInterface(){
    $(this.screen,'#cannon-editor-close').addEventListener('click',()=>this.close());
    $(this.screen,'#cannon-editor-reset').addEventListener('click',()=>this.resetDesign());
    this.saveButton.addEventListener('click',()=>this.save());
    this.testSoundButton.addEventListener('click',()=>this.testSound());
    $$ (this.screen,'[data-cannon-mode]').forEach((button)=>{
      button.addEventListener('click',()=>this.setMode(button.dataset.cannonMode));
    });
    $$ (this.screen,'[data-cannon-preview-mode]').forEach((button)=>{
      button.addEventListener('click',()=>this.setPreviewMode(button.dataset.cannonPreviewMode));
    });
    $$ (this.screen,'[data-cannon-sound-mode]').forEach((button)=>{
      button.addEventListener('click',()=>this.setSoundMode(button.dataset.cannonSoundMode));
    });
    this.vehicleSelect.addEventListener('change',()=>{
      savePreviewVehiclePreference(this.vehicleSelect.value);
      if(this.previewMode==='vehicle')this.loadMountedVehicle(this.vehicleSelect.value);
    });
    this.templateSelect.addEventListener('change',()=>{
      const template=CANNONS.find((item)=>item.id===this.templateSelect.value)||CANNONS[0];
      this.design=cloneDesign(template);
      this.syncControls();
      if(this.mode==='procedural')this.rebuild();
    });
    this.styleSelect.addEventListener('change',()=>{
      const template={
        ...this.design,
        style:this.styleSelect.value,
        barrelLength:undefined,
        barrelRadius:undefined,
      };
      this.design=cloneDesign(template);
      this.syncControls();
      if(this.mode==='procedural')this.rebuild();
    });
    $$ (this.screen,'[data-cannon-design]').forEach((input)=>{
      input.addEventListener('input',()=>{
        const key=input.dataset.cannonDesign;
        this.design[key]=(input.type==='color'||input.tagName==='SELECT')?input.value:Number(input.value);
        this.syncOutput(input);
        if(this.mode==='procedural')this.rebuild();
        else if(key==='modelScale'||key.startsWith('modelRotation')){
          if(this.previewMode==='vehicle')this.updateMountedCannon(true);
          else this.refreshImportedPreview(true);
        }
      });
    });
    this.fileInput.addEventListener('change',()=>this.loadGlbFile(this.fileInput.files[0]));
    this.soundFileInput.addEventListener('change',()=>this.loadSoundFile(this.soundFileInput.files[0]));
    this.defaultProjectileSelect.addEventListener('change',()=>{
      this.design.defaultProjectileId=this.defaultProjectileSelect.value;
    });
    this.autoRotateInput.addEventListener('change',()=>{
      saveAutoRotatePreference(this.autoRotateInput.checked);
    });
    window.addEventListener('resize',()=>this.resize());
  }

  syncOutput(input){
    input.syncNumberControl?.();
    const output=$(this.screen,`[data-cannon-output="${input.dataset.cannonDesign}"]`);
    if(!output)return;
    const value=this.design[input.dataset.cannonDesign];
    const decimals=Number(input.step)<.01?3:2;
    output.textContent=Number.isInteger(value)?value:Number(value).toFixed(decimals);
  }

  syncControls(){
    $$ (this.screen,'[data-cannon-design]').forEach((input)=>{
      input.value=this.design[input.dataset.cannonDesign];
      this.syncOutput(input);
    });
    this.styleSelect.value=this.design.style;
    this.defaultProjectileSelect.value=this.design.defaultProjectileId;
    $$ (this.screen,'[data-cannon-mode]').forEach((button)=>{
      button.classList.toggle('selected',button.dataset.cannonMode===this.mode);
    });
    $(this.screen,'[data-cannon-panel="procedural"]').classList.toggle(
      'hidden',
      this.mode!=='procedural',
    );
    $(this.screen,'[data-cannon-panel="glb"]').classList.toggle(
      'hidden',
      this.mode!=='glb',
    );
    $$ (this.screen,'[data-cannon-sound-mode]').forEach((button)=>{
      button.classList.toggle('selected',button.dataset.cannonSoundMode===this.soundMode);
    });
    $$ (this.screen,'[data-cannon-sound-panel]').forEach((panel)=>{
      panel.classList.toggle('hidden',panel.dataset.cannonSoundPanel!==this.soundMode);
    });
    $$ (this.screen,'[data-cannon-style-panel]').forEach((panel)=>{
      const styles=panel.dataset.cannonStylePanel.split(' ');
      panel.classList.toggle('hidden',!styles.includes(this.design.style));
    });
    this.syncPreviewControls();
    this.updateActionButtons();
  }

  updateActionButtons(){
    this.saveButton.disabled=(
      (this.mode==='glb'&&!this.assetFile)
      ||(this.soundMode==='mp3'&&!this.soundFile)
    );
    this.testSoundButton.disabled=this.soundMode==='mp3'&&!this.soundFile;
  }

  syncPreviewControls(){
    $$ (this.screen,'[data-cannon-preview-mode]').forEach((button)=>{
      button.classList.toggle('selected',button.dataset.cannonPreviewMode===this.previewMode);
    });
    this.vehicleControls.classList.toggle('hidden',this.previewMode!=='vehicle');
  }

  replacePreview(object){
    if(this.previewObject){
      this.previewMount.remove(this.previewObject);
      disposeObject(this.previewObject);
    }
    this.mountedRig=null;
    this.mountedVehicle=null;
    this.mountedCannonMount=null;
    this.mountedCannonObject=null;
    this.previewObject=object;
    this.previewMount.add(object);
  }

  createImportedPreview(paintFinish=null){
    if(!this.importedPreview)return null;
    const model=cloneGlbPreview(this.importedPreview);
    if(paintFinish){
      prepareVehicleFinishMaterials(model);
      applyVehicleBodyFinish(model,paintFinish);
    }
    model.rotation.set(
      THREE.MathUtils.degToRad(this.design.modelRotationX),
      THREE.MathUtils.degToRad(this.design.modelRotationY),
      THREE.MathUtils.degToRad(this.design.modelRotationZ),
    );
    model.updateMatrixWorld(true);
    let bounds=new THREE.Box3().setFromObject(model);
    const size=bounds.getSize(new THREE.Vector3());
    model.scale.multiplyScalar(
      (1.15*this.design.modelScale)/(Math.max(size.x,size.y,size.z)||1),
    );
    model.updateMatrixWorld(true);
    bounds=new THREE.Box3().setFromObject(model);
    const center=bounds.getCenter(new THREE.Vector3());
    model.position.set(-center.x,-bounds.min.y,-bounds.max.z);
    const oriented=new THREE.Group();
    oriented.name='oriented_cannon_preview';
    oriented.add(model);
    return oriented;
  }

  refreshImportedPreview(showStatus=false){
    const preview=this.createImportedPreview();
    if(!preview)return;
    this.replacePreview(preview);
    if(showStatus){
      this.setStatus(
        `Escala ${this.design.modelScale.toFixed(2)}× · orientación X ${this.design.modelRotationX}° · Y ${this.design.modelRotationY}° · Z ${this.design.modelRotationZ}°.`,
      );
    }
  }

  currentCannonSource(paintFinish=null){
    if(this.mode==='procedural')return createCannon(this.design);
    if(this.mode==='glb'&&this.importedPreview)return this.createImportedPreview(paintFinish);
    return null;
  }

  configureStandaloneStage(){
    this.floor.position.y=0;
    this.floor.scale.setScalar(1);
    this.grid.position.y=.005;
    this.previewMount.rotation.x=0;
    this.previewMount.rotation.z=0;
    this.camera.position.set(3.2,2.25,4.2);
    this.controls.target.set(0,.28,-.2);
    this.controls.minDistance=1.8;
    this.controls.maxDistance=8;
    this.camera.near=.03;
    this.camera.far=40;
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  configureMountedStage(){
    this.floor.position.y=0;
    this.floor.scale.setScalar(2.5);
    this.grid.position.y=.005;
    this.previewMount.rotation.x=0;
    this.previewMount.rotation.z=0;
    this.controls.minDistance=3.2;
    this.controls.maxDistance=24;
    this.frameMountedVehicle();
  }

  frameMountedVehicle(){
    if(!this.previewObject)return;
    this.previewMount.updateMatrixWorld(true);
    const bounds=new THREE.Box3().setFromObject(this.previewObject);
    if(bounds.isEmpty())return;
    const sphere=bounds.getBoundingSphere(new THREE.Sphere());
    const verticalFov=THREE.MathUtils.degToRad(this.camera.fov);
    const horizontalFov=2*Math.atan(
      Math.tan(verticalFov*.5)*Math.max(this.camera.aspect,.2),
    );
    const fittingFov=Math.min(verticalFov,horizontalFov);
    const distance=Math.max(
      5,
      sphere.radius/Math.max(Math.sin(fittingFov*.5),.08)*1.16,
    );
    const direction=new THREE.Vector3(5.7,3.15,-6.2).normalize();
    this.controls.target.copy(sphere.center);
    this.camera.position.copy(sphere.center).addScaledVector(direction,distance);
    this.camera.near=Math.max(.01,distance/250);
    this.camera.far=Math.max(80,distance*12);
    this.camera.updateProjectionMatrix();
    this.controls.update();
  }

  updateMountedCannon(showStatus=false){
    if(!this.mountedCannonMount)return false;
    if(this.mountedCannonObject){
      this.mountedCannonMount.remove(this.mountedCannonObject);
      disposeObject(this.mountedCannonObject);
      this.mountedCannonObject=null;
    }
    const cannon=this.currentCannonSource(this.mountedRig?.profile?.body?.finish);
    if(!cannon)return false;
    this.mountedCannonObject=cannon;
    this.mountedCannonMount.add(cannon);
    this.frameMountedVehicle();
    if(showStatus){
      this.setStatus(
        `Vista actualizada: cañón montado en ${this.mountedVehicle?.name||'el auto seleccionado'}.`,
      );
    }
    return true;
  }

  rebuild(){
    if(this.previewMode==='vehicle'){
      this.updateMountedCannon(true);
      return;
    }
    this.configureStandaloneStage();
    this.replacePreview(createCannon(this.design));
    this.setStatus('Vista procedural actualizada. Los cambios se guardan como parámetros editables.');
  }

  async refreshVehicleOptions(){
    const preferred=this.vehicleSelect.value||readPreviewVehiclePreference();
    try{
      const vehicles=(await this.callbacks.onListVehicles?.()||[])
        .filter((item)=>item.type==='vehicle')
        .sort((first,second)=>(first.name||first.id).localeCompare(second.name||second.id,'es'));
      this.vehicleSelect.replaceChildren();
      if(!vehicles.length){
        const option=document.createElement('option');
        option.value='';
        option.textContent='No hay autos guardados';
        this.vehicleSelect.append(option);
        this.vehicleSelect.disabled=true;
        this.vehicleNote.textContent='Guardá al menos un auto calibrado para probar el cañón montado.';
        this.vehicleNote.dataset.state='warning';
        this.vehicleOptionsLoaded=true;
        return;
      }
      vehicles.forEach((item)=>{
        const option=document.createElement('option');
        option.value=item.id;
        option.textContent=item.name||item.id;
        this.vehicleSelect.append(option);
      });
      const selected=vehicles.some((item)=>item.id===preferred)?preferred:vehicles[0].id;
      this.vehicleSelect.value=selected;
      this.vehicleSelect.disabled=false;
      savePreviewVehiclePreference(selected);
      this.vehicleNote.textContent='El cañón se coloca en el punto de montaje calibrado del auto.';
      this.vehicleNote.dataset.state='ready';
      this.vehicleOptionsLoaded=true;
      if(this.previewMode==='vehicle')await this.loadMountedVehicle(selected);
    }catch(error){
      console.error(error);
      this.vehicleSelect.replaceChildren();
      const option=document.createElement('option');
      option.value='';
      option.textContent='No se pudieron cargar los autos';
      this.vehicleSelect.append(option);
      this.vehicleSelect.disabled=true;
      this.vehicleNote.textContent=error.message||'No se pudo leer el catálogo de autos.';
      this.vehicleNote.dataset.state='warning';
      this.vehicleOptionsLoaded=true;
    }
  }

  async setPreviewMode(mode){
    if(mode!=='cannon'&&mode!=='vehicle')return;
    if(mode===this.previewMode&&mode==='vehicle'&&this.mountedRig)return;
    this.previewMode=mode;
    this.syncPreviewControls();
    if(mode==='cannon'){
      this.vehicleLoadToken+=1;
      const cannon=this.currentCannonSource();
      this.configureStandaloneStage();
      this.replacePreview(cannon||new THREE.Group());
      this.setStatus(
        cannon
          ?this.mode==='glb'?'Vista individual del cañón GLB.':'Vista individual del cañón.'
          :'Importá un cañón GLB para previsualizarlo.',
        cannon?'ready':'warning',
      );
      return;
    }
    this.configureMountedStage();
    if(!this.vehicleOptionsLoaded){
      await this.refreshVehicleOptions();
      return;
    }
    await this.loadMountedVehicle(this.vehicleSelect.value);
  }

  async loadMountedVehicle(id){
    const loadToken=++this.vehicleLoadToken;
    if(!id){
      this.replacePreview(new THREE.Group());
      this.configureMountedStage();
      this.setStatus('No hay autos disponibles para la vista de montaje.','warning');
      return;
    }
    this.vehicleSelect.disabled=true;
    this.vehicleNote.textContent='Cargando carrocería y punto de montaje…';
    this.vehicleNote.dataset.state='ready';
    this.setStatus('Preparando el cañón montado en el auto…');
    try{
      const item=await this.callbacks.onLoadVehicle?.(id);
      if(loadToken!==this.vehicleLoadToken)return;
      if(!item?.bodyFile)throw new Error('El auto seleccionado no tiene una carrocería GLB disponible.');
      const gltf=await this.loader.parseAsync(await item.bodyFile.arrayBuffer(),'');
      const scene=gltf.scene||gltf.scenes?.[0];
      if(!scene)throw new Error('La carrocería seleccionada no contiene una escena válida.');
      if(loadToken!==this.vehicleLoadToken){
        disposeObject(scene);
        return;
      }
      const rig=new VehicleAssetRig(item.profile);
      rig.setBody(scene,item.bodyFile.name);
      rig.setWheel(createDefaultCalibrationWheel(),'rueda-de-referencia');
      const vehicleVisualScale=(
        Number(item.profile?.game?.visualScale)
        ||DEFAULT_IMPORTED_VEHICLE_VISUAL_SCALE
      );
      rig.root.scale.setScalar(vehicleVisualScale);
      const mountedPreview=new THREE.Group();
      mountedPreview.name='cannon_vehicle_preview';
      mountedPreview.add(rig.root);
      const cannonMount=new THREE.Group();
      cannonMount.name='cannon_preview_mount';
      const socket=rig.profile.sockets.weapon;
      cannonMount.position.set(
        socket.x*vehicleVisualScale,
        socket.y*vehicleVisualScale,
        socket.z*vehicleVisualScale,
      );
      mountedPreview.add(cannonMount);
      const cannon=this.currentCannonSource(rig.profile.body.finish);
      if(cannon)cannonMount.add(cannon);
      this.replacePreview(mountedPreview);
      this.mountedRig=rig;
      this.mountedVehicle=item;
      this.mountedCannonMount=cannonMount;
      this.mountedCannonObject=cannon;
      this.configureMountedStage();
      this.vehicleNote.textContent=(
        `Montado en ${item.name||item.id}: escala del juego ${vehicleVisualScale.toFixed(2)}×`
        +' y punto de arma calibrado'
        +(this.mode==='glb'?', con el acabado de pintura del auto.':'.')
      );
      this.vehicleNote.dataset.state='success';
      this.setStatus(
        cannon
          ?[
            `Cañón montado en ${item.name||item.id}.`,
            this.mode==='glb'?'El GLB hereda el acabado configurado para la carrocería.':'',
            'Podés ajustar su escala y orientación en esta vista.',
          ].filter(Boolean).join(' ')
          :'Auto cargado. Importá un cañón GLB para verlo en el punto de montaje.',
        cannon?'success':'warning',
      );
    }catch(error){
      if(loadToken!==this.vehicleLoadToken)return;
      console.error(error);
      this.replacePreview(new THREE.Group());
      this.configureMountedStage();
      this.vehicleNote.textContent=error.message||'No se pudo preparar este auto.';
      this.vehicleNote.dataset.state='warning';
      this.setStatus(error.message||'No se pudo cargar el auto seleccionado.','error');
    }finally{
      if(loadToken===this.vehicleLoadToken)this.vehicleSelect.disabled=false;
    }
  }

  setMode(mode){
    if(mode!=='procedural'&&mode!=='glb')return;
    this.mode=mode;
    this.syncControls();
    if(mode==='procedural'){
      this.rebuild();
    }else if(this.importedPreview){
      if(this.previewMode==='vehicle'){
        this.updateMountedCannon();
        this.setStatus(`Cañón GLB montado en ${this.mountedVehicle?.name||'el auto seleccionado'}.`);
      }else{
        this.configureStandaloneStage();
        this.refreshImportedPreview();
      }
    }else{
      if(this.previewMode==='vehicle')this.updateMountedCannon();
      this.setStatus('Elegí un cañón GLB para validarlo y previsualizarlo.','ready');
    }
  }

  async loadGlbFile(file){
    if(!file)return;
    this.setStatus('Analizando el cañón GLB…','ready');
    try{
      const result=await loadCatalogGlb(file,'cannon');
      disposeObject(this.importedPreview);
      this.importedPreview=result.preview;
      this.assetFile=file;
      this.assetStats=result.stats;
      if(this.previewMode==='vehicle')this.updateMountedCannon();
      else{
        this.configureStandaloneStage();
        this.refreshImportedPreview();
      }
      this.fileName.textContent=file.name;
      const warningText=result.stats.warnings.length?' · revisar':'';
      this.fileInfo.textContent=`${result.stats.meshes} mallas · ${formatGlbTriangles(result.stats.triangles)}${warningText}`;
      this.updateActionButtons();
      this.setStatus(
        result.stats.warnings[0]
          ?`GLB cargado. ${result.stats.warnings.join(' ')}`
          :'GLB validado y normalizado. El montaje queda en el origen y el disparo apunta hacia −Z.',
        result.stats.warnings.length?'warning':'ready',
      );
    }catch(error){
      console.error(error);
      this.assetFile=null;
      this.assetStats=null;
      this.fileName.textContent='Elegir archivo GLB';
      this.fileInfo.textContent='No se pudo validar el archivo';
      this.updateActionButtons();
      this.setStatus(error.message||'No se pudo abrir el cañón GLB.','error');
    }
  }

  setSoundMode(mode){
    if(mode!=='procedural'&&mode!=='mp3')return;
    this.soundMode=mode;
    this.syncControls();
    this.setStatus(
      mode==='mp3'
        ?this.soundFile
          ?'Disparo MP3 listo para probar.'
          :'Elegí un MP3 corto para usarlo como sonido de disparo.'
        :'Disparo procedural listo para probar.',
      this.soundFile&&mode==='mp3'?'success':'ready',
    );
  }

  async loadSoundFile(file){
    if(!file)return;
    this.testSoundButton.disabled=true;
    this.saveButton.disabled=true;
    this.setStatus('Analizando el disparo MP3…','ready');
    try{
      const stats=await inspectOneShotSample(file);
      this.soundMode='mp3';
      this.soundFile=file;
      this.soundStats=stats;
      this.soundFileName.textContent=file.name;
      this.soundFileInfo.textContent=[
        `${stats.duration.toFixed(2)} s`,
        `${stats.sampleRate} Hz`,
        stats.channels===1?'mono':`${stats.channels} canales`,
        stats.warnings.length?'revisar duración':'listo para disparar',
      ].join(' · ');
      this.syncControls();
      this.setStatus(
        stats.warnings[0]||'MP3 validado. Se reproducirá desde el comienzo en cada disparo.',
        stats.warnings.length?'warning':'success',
      );
    }catch(error){
      console.error(error);
      this.soundFile=null;
      this.soundStats=null;
      this.soundFileInput.value='';
      this.soundFileName.textContent='Elegir archivo MP3';
      this.soundFileInfo.textContent='No se pudo validar el archivo';
      this.syncControls();
      this.setStatus(error.message||'No se pudo abrir el MP3.','error');
    }
  }

  async testSound(){
    if(this.soundMode==='mp3'&&!this.soundFile){
      this.setStatus('Cargá un MP3 antes de probar el disparo.','warning');
      return;
    }
    this.testSoundButton.disabled=true;
    try{
      if(this.soundMode==='mp3')await previewOneShotSample(this.soundFile,this.design);
      else previewOneShot(this.design,'sound');
      this.setStatus(
        this.soundMode==='mp3'
          ?'Reproduciendo el disparo MP3 con el volumen y tono configurados.'
          :'Reproduciendo el disparo procedural.',
        'success',
      );
    }catch(error){
      console.error(error);
      this.setStatus(error.message||'No se pudo reproducir el disparo.','error');
    }finally{
      this.updateActionButtons();
    }
  }

  setStatus(message,state='ready'){
    this.status.textContent=message;
    this.status.dataset.state=state;
  }

  prepareStandaloneThumbnail(){
    if(this.previewMode!=='vehicle')return ()=>{};
    const cannon=this.currentCannonSource();
    if(!cannon)return ()=>{};
    const previousVisibility=this.previewObject?.visible;
    const floorY=this.floor.position.y;
    const floorScale=this.floor.scale.clone();
    const gridY=this.grid.position.y;
    const mountRotation=this.previewMount.rotation.clone();
    if(this.previewObject)this.previewObject.visible=false;
    this.floor.position.y=0;
    this.floor.scale.setScalar(1);
    this.grid.position.y=.005;
    this.previewMount.rotation.x=0;
    this.previewMount.rotation.z=0;
    this.previewMount.add(cannon);
    return ()=>{
      this.previewMount.remove(cannon);
      disposeObject(cannon);
      if(this.previewObject)this.previewObject.visible=previousVisibility;
      this.floor.position.y=floorY;
      this.floor.scale.copy(floorScale);
      this.grid.position.y=gridY;
      this.previewMount.rotation.copy(mountRotation);
    };
  }

  resetDesign(){
    const template=CANNONS.find((item)=>item.id===this.templateSelect.value)||CANNONS[0];
    this.design=cloneDesign(template);
    this.syncControls();
    if(this.mode==='procedural')this.rebuild();
    else if(this.previewMode==='vehicle')this.updateMountedCannon(true);
    else this.refreshImportedPreview(true);
  }

  clearWorkspace(){
    this.mode='procedural';
    this.assetFile=null;
    this.assetStats=null;
    this.soundMode='procedural';
    this.soundFile=null;
    this.soundStats=null;
    disposeObject(this.importedPreview);
    this.importedPreview=null;
    this.fileInput.value='';
    this.fileName.textContent='Elegir archivo GLB';
    this.fileInfo.textContent='Montaje en origen · disparo hacia −Z';
    this.soundFileInput.value='';
    this.soundFileName.textContent='Elegir archivo MP3';
    this.soundFileInfo.textContent='Sonido corto, limpio y sin silencio inicial';
    this.templateSelect.value=CANNONS[0].id;
    this.design=cloneDesign(CANNONS[0]);
    this.syncControls();
    this.rebuild();
  }

  setProjectileOptions(items=[]){
    const projectiles=items.length?items:PROJECTILES;
    this.defaultProjectileSelect.innerHTML=projectiles.map((item)=>`<option value="${item.id}">${item.name||item.id}</option>`).join('');
    if(!projectiles.some((item)=>item.id===this.design.defaultProjectileId)){
      this.design.defaultProjectileId=projectiles[0]?.id||PROJECTILES[0].id;
    }
    this.defaultProjectileSelect.value=this.design.defaultProjectileId;
  }

  open(){
    this.active=true;
    this.callbacks.onOpen?.();
    this.refreshVehicleOptions();
    requestAnimationFrame(()=>this.resize());
  }

  close(){
    this.active=false;
    this.callbacks.onClose?.();
  }

  async loadRecord(item){
    disposeObject(this.importedPreview);
    this.importedPreview=null;
    this.fileInput.value='';
    this.fileName.textContent='Elegir archivo GLB';
    this.fileInfo.textContent='Montaje en origen · disparo hacia −Z';
    this.soundFileInput.value='';
    this.soundFileName.textContent='Elegir archivo MP3';
    this.soundFileInfo.textContent='Sonido corto, limpio y sin silencio inicial';
    this.mode=item.profile?.mode||'procedural';
    this.design=cloneDesign(item.profile?.design||CANNONS[0]);
    this.assetFile=item.assetFile||null;
    this.assetStats=item.profile?.asset||null;
    this.soundMode=item.profile?.sound?.mode==='mp3'?'mp3':'procedural';
    this.soundFile=item.audioFile||null;
    this.soundStats=item.profile?.sound||null;
    this.syncControls();
    if(this.mode==='glb'&&this.assetFile)await this.loadGlbFile(this.assetFile);
    else this.rebuild();
    if(this.soundMode==='mp3'&&this.soundFile){
      await this.loadSoundFile(this.soundFile);
    }else{
      this.updateActionButtons();
    }
  }

  async save(){
    const id=cleanId($(this.screen,'#cannon-editor-id').value);
    $(this.screen,'#cannon-editor-id').value=id;
    if(this.mode==='glb'&&!this.assetFile){
      this.setStatus('Cargá un GLB antes de guardar un cañón importado.','warning');
      return;
    }
    if(this.soundMode==='mp3'&&!this.soundFile){
      this.setStatus('Cargá un MP3 antes de guardar el disparo importado.','warning');
      return;
    }
    this.saveButton.disabled=true;
    try{
      const result=await this.callbacks.onSave?.({
        id,
        mode:this.mode,
        design:cloneDesign(this.design),
        assetFile:this.assetFile,
        assetStats:this.assetStats,
        soundMode:this.soundMode,
        soundFile:this.soundFile,
        soundStats:this.soundStats,
      });
      this.setStatus(result?.message||'Cañón guardado.','success');
    }catch(error){
      console.error(error);
      this.setStatus(error.message||'No se pudo guardar el cañón.','error');
    }finally{
      this.updateActionButtons();
    }
  }

  resize(){
    if(!this.active)return;
    const width=this.canvas.clientWidth;
    const height=this.canvas.clientHeight;
    if(!width||!height)return;
    this.renderer.setSize(width,height,false);
    this.camera.aspect=width/height;
    this.camera.updateProjectionMatrix();
  }

  animate(){
    requestAnimationFrame(()=>this.animate());
    if(!this.active)return;
    this.resize();
    this.controls.update();
    if(this.autoRotateInput.checked)this.previewMount.rotation.y+=.0025;
    this.renderer.render(this.scene,this.camera);
  }
}
